<template>
  <VdPage class="test-device-assignations-list-page" title="Test device assignations" :loading="loading">
    <template #actions>
      <VdInput
        v-model="searchTerm"
        type="text"
        class="search-input"
        placeholder="Look for a device..."
        data-cy="search-input"
      >
        <template #prepend>
          <VdIcon store="fa" name="search" />
        </template>
      </VdInput>
      <Button label="Add test device" icon="pi pi-mobile" style="width: 245px" @click="onOpenAddDevice" />
    </template>

    <div class="test-device-assignations-info">
      <VdIcon store="fa" name="search" />
      Adding your device on VoodooTune allows you to use the VoodooSauce debugger and simulate AB test & segmentations.
    </div>

    <div class="test-device-assignations-list">
      <keep-alive v-if="!loading && filteredDevices.length === 0">
        <vd-empty :title="searchTerm ? 'not found' : 'No devices currently assigned'">
          <div v-if="searchTerm">
            <p>Your search for a device has no results</p>
          </div>
          <div v-else>
            <p>Sorry, there is no data.</p>
            <p v-if="hasTestDevices">Assign some devices to A/B tests cohorts and come back.</p>
            <div v-else>
              <p>Add some devices to your user settings, assign them to A/B tests and come back.</p>
              <a
                style="text-decoration: underline"
                href="https://www.notion.so/voodoo/Device-Management-112a0b481db480738dd4c6430aa90cef"
                target="_blank"
                rel="noopener noreferrer"
              >
                How do I add a test device ?
              </a>
            </div>
          </div>
          <template v-if="!hasTestDevices" #actions>
            <Button label="Add test device" icon="pi pi-mobile" @click="onOpenAddDevice" />
          </template>
        </vd-empty>
      </keep-alive>

      <div v-else>
        <template v-if="userTestDevices.length">
          <span class="test-devices-title">
            <span>My devices & assignations</span>
            <span>({{ userTestDevices.length }})</span>
          </span>
          <DevicesListItem
            v-for="device in userTestDevices"
            :key="device.id"
            :device="device"
            :assignations="device.assignations"
            @remove-assignations="onRemoveDeviceAssignations"
            @delete="onDeviceDelete"
          />
        </template>

        <template v-if="externalTestDevices.length">
          <span class="test-devices-title">
            <span>External devices & assignations</span>
            <span>({{ externalTestDevices.length }})</span>
          </span>
          <DevicesListItem
            v-for="device in externalTestDevices"
            :key="device.id"
            :device="device"
            :assignations="device.assignations"
            @remove-assignations="onRemoveDeviceAssignations"
            @delete="onDeviceDelete"
          />
        </template>
      </div>
    </div>
  </VdPage>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdInput, VdPage } from "@voodoo.io/voodoo-ui";
import Button from "primevue/button";
import { computed } from "vue";
import { useRouter } from "vue-router";

import {
  queryNames,
  TestDeviceAssignationsListPageTestDeviceAssignationFragment,
  useMutationResponse,
  useTestDeviceAssignationsListPageDeleteAssignationsMutation,
  useTestDeviceAssignationsListPageDeleteMutation,
  useTestDeviceAssignationsListPageQuery,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import useSearchFilter from "@/hooks/search-filter";
import links from "@/router/links";

import DevicesListItem from "./components/DevicesListItem/DevicesListItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const { confirm } = useMessages();

const { result, loading: queryTestersLoading } = useTestDeviceAssignationsListPageQuery(
  { gameId: props.gameId },
  { fetchPolicy: "network-only" }
);

const hasTestDevices = computed(() => (result.value?.testDevices ?? []).length);

const devices = computed(() => {
  const testDevices = result.value?.testDevices ?? [];
  const assignations = result.value?.game?.testDeviceAssignations ?? [];

  const assignationsByDeviceId = assignations.reduce<
    Record<string, TestDeviceAssignationsListPageTestDeviceAssignationFragment[]>
  >((acc, assignation) => {
    const deviceId = assignation.testDevice.id as string;
    return {
      ...acc,
      [deviceId]: [...(acc[deviceId] ?? []), assignation],
    };
  }, {});

  return testDevices.map((o) => ({ ...o, assignations: assignationsByDeviceId[o.id] }));
});

/**
 * Filter the devices from the searchTerm
 */
const { searchTerm, filteredItems: filteredDevices } = useSearchFilter(devices, ["id", "name"]);

const externalTestDevices = computed(() => filteredDevices.value.filter((device) => device.external));
const userTestDevices = computed(() => filteredDevices.value.filter((device) => !device.external));

/**
 *  Delete a test device
 */
const { mutate: deleteDevice, loading: deleteDeviceLoading } = useTestDeviceAssignationsListPageDeleteMutation({
  refetchQueries: [queryNames.TestDeviceAssignationsListPage],
});
const onDeviceDelete = async (testDeviceId: string) => {
  const confirmed = await confirm("Do you really want to delete this device?");
  if (confirmed) {
    const response = await deleteDevice({ testDeviceId });
    useMutationResponse(response, (resp) => resp?.deleteTestDevice);
  }
};

/**
 * Remove assignation
 */
const { mutate: removeTestDeviceAssignation, loading: deleteDeviceAssignationLoading } =
  useTestDeviceAssignationsListPageDeleteAssignationsMutation({
    refetchQueries: [queryNames.TestDeviceAssignationsListPage],
  });

const onRemoveDeviceAssignations = async ({ deviceId }: { deviceId: string }) => {
  const response = await removeTestDeviceAssignation({ gameId: props.gameId, deviceId });
  useMutationResponse(response, (resp) => resp?.deleteTestDeviceAssignations);
};

const loading = computed(
  () => queryTestersLoading.value || deleteDeviceLoading.value || deleteDeviceAssignationLoading.value
);

const onOpenAddDevice = () => {
  router.push({ name: links.addDevice, params: { gameId: props.gameId } });
};
</script>

<style lang="scss" scoped>
.test-devices-title {
  span {
    display: inline-block;
    margin-bottom: size("L");
  }

  span:first-of-type {
    font-size: font("L");
    font-weight: bold;
  }

  span:last-of-type {
    margin-left: size("XS");
    color: var(--vd-color-grey-light);
  }
}

.test-device-assignations-info {
  display: flex;
  align-items: center;
  background: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-default);
  padding: 0.75rem;
  padding-left: 1rem;
  margin-bottom: 2rem;

  svg {
    margin-right: 0.75rem;
    color: var(--vd-color-blue-default);
  }
}
</style>
